'use client';

import { isEqual } from 'lodash';
import { useEffect, useRef } from 'react';

import { type Account, type FullWorkspace } from '@air/api/types';
import { useLaunchDarklyContext, usePluginAnalytics, useWorkspace } from '@air/plugin-ui';
import { useAccount } from '@air/utils-auth';
import { Bugsnag } from '@air/utils-error';

import { useURLWorkspaceId } from '~/hooks/url/useURLWorkspaceId';

export const useConfigureThirdPartyServices = () => {
  const selectedWorkspaceId = useURLWorkspaceId();
  const { data: workspace } = useWorkspace({ workspaceId: selectedWorkspaceId });
  const { data: account } = useAccount();
  const { identify: launchDarklyIdentify } = useLaunchDarklyContext();
  const { identify: analyticsIdentify, setGlobalEventProps } = usePluginAnalytics();

  const workspaceRef = useRef<FullWorkspace>();
  const accountRef = useRef<Account>();

  useEffect(() => {
    const isWorkspaceChanged = workspace && (!workspaceRef.current || !isEqual(workspaceRef.current, workspace));
    const isAccountChanged = account && (!accountRef.current || !isEqual(accountRef.current, account));

    if (workspace && account && (isWorkspaceChanged || isAccountChanged)) {
      workspaceRef.current = workspace;
      accountRef.current = account;

      Bugsnag.setUser(account.authId ?? account.id, account.email, `${account.firstName} ${account.lastName}`);

      launchDarklyIdentify({
        anonymous: false,
        email: account.email,
        firstName: account.firstName,
        key: account.authId ?? account.id,
        lastName: account.lastName,
        workspaceId: selectedWorkspaceId,
        workspaceName: workspace.name,
        workspacePlan: workspace.plan,
      });

      analyticsIdentify(account.authId ?? account.id, {
        account_type: account.type,
        email: account.email,
        first_name: account.firstName,
        last_name: account.lastName,
      });

      setGlobalEventProps({
        accountType: account.type,
        airAccountId: account.id,
        app_name: 'PremierePlugin',
        userEmail: account.email,
        workspaceId: selectedWorkspaceId,
        workspaceName: workspace.name,
      });
    }
  }, [workspace, account, selectedWorkspaceId, analyticsIdentify, launchDarklyIdentify, setGlobalEventProps]);
};
